/**
 * Static translations facade.
 *
 * Translation namespaces are split by domain under `src/lib/translations/`:
 *   - pages       (contact, about, shipping, returns, pages, privacy, terms, giftCard, trackOrder, ringSizeGuide, accessibility, cookiePolicy)
 *   - auth-profile (auth, profile, profileTour, profileGreeting, orderHistory, favorites, repairOrders, userMessages, wishlistPrompt)
 *   - commerce    (productCard, cartDrawer, search, variantPicker, engraving, tryOn, imageSearch)
 *   - layout      (common, footer, header, newsletter, homepage, whyUs, whyChoose, seo)
 *   - dialogs     (ringSizeDialog, cookieBanner, cookiePreferences, confirmDialog)
 *   - admin       (admin, adminMenu, adminUsers, adminColor, adminSearch, adminBot, adminPromo, adminFaq, adminContact, adminCustomerMsg, adminSms, adminTV)
 *
 * This file keeps the public API (`useStaticTranslation`, `getStaticTranslation`)
 * unchanged so all existing imports continue to work.
 */
import { getTranslateLang } from "@/lib/translateUtils";
import { useState, useEffect, useCallback } from "react";
import * as pagesNs from "./translations/pages";
import * as authProfileNs from "./translations/auth-profile";
import * as commerceNs from "./translations/commerce";
import * as layoutNs from "./translations/layout";
import * as dialogsNs from "./translations/dialogs";
import * as adminNs from "./translations/admin";

type LangCode = "en" | "ru" | "ar";

const allSections = {
  // pages
  contact: pagesNs.contact,
  about: pagesNs.about,
  shipping: pagesNs.shipping,
  returns: pagesNs.returns,
  pages: pagesNs.pages,
  privacy: pagesNs.privacy,
  terms: pagesNs.terms,
  giftCard: pagesNs.giftCard,
  trackOrder: pagesNs.trackOrder,
  ringSizeGuide: pagesNs.ringSizeGuide,
  accessibility: pagesNs.accessibility,
  cookiePolicy: pagesNs.cookiePolicy,
  // auth-profile
  auth: authProfileNs.auth,
  profile: authProfileNs.profile,
  profileTour: authProfileNs.profileTour,
  profileGreeting: authProfileNs.profileGreeting,
  orderHistory: authProfileNs.orderHistory,
  favorites: authProfileNs.favorites,
  repairOrders: authProfileNs.repairOrders,
  userMessages: authProfileNs.userMessages,
  wishlistPrompt: authProfileNs.wishlistPrompt,
  // commerce
  productCard: commerceNs.productCard,
  cartDrawer: commerceNs.cartDrawer,
  search: commerceNs.search,
  variantPicker: commerceNs.variantPicker,
  engraving: commerceNs.engraving,
  tryOn: commerceNs.tryOn,
  imageSearch: commerceNs.imageSearch,
  // layout
  common: layoutNs.common,
  footer: layoutNs.footer,
  header: layoutNs.header,
  newsletter: layoutNs.newsletter,
  homepage: layoutNs.homepage,
  whyUs: layoutNs.whyUs,
  whyChoose: layoutNs.whyChoose,
  seo: layoutNs.seo,
  // dialogs
  ringSizeDialog: dialogsNs.ringSizeDialog,
  cookieBanner: dialogsNs.cookieBanner,
  cookiePreferences: dialogsNs.cookiePreferences,
  confirmDialog: dialogsNs.confirmDialog,
  // admin
  admin: adminNs.admin,
  adminMenu: adminNs.adminMenu,
  adminUsers: adminNs.adminUsers,
  adminColor: adminNs.adminColor,
  adminSearch: adminNs.adminSearch,
  adminBot: adminNs.adminBot,
  adminPromo: adminNs.adminPromo,
  adminFaq: adminNs.adminFaq,
  adminContact: adminNs.adminContact,
  adminCustomerMsg: adminNs.adminCustomerMsg,
  adminSms: adminNs.adminSms,
  adminTV: adminNs.adminTV,
} as const;

type SectionName = keyof typeof allSections;

/**
 * Get translations for a specific section based on the current Google Translate language.
 * Returns null if the language is Hebrew (default).
 */
export function getStaticTranslation<S extends SectionName>(
  section: S
): (typeof allSections)[S][LangCode] | null {
  const lang = getTranslateLang();
  if (!lang || lang === "he") return null;

  const sectionData = allSections[section];
  return (sectionData as any)[lang] || null;
}

/**
 * React hook that provides translations for a section, reactive to language changes.
 * Returns a `t(key)` function that returns the translated text or the Hebrew fallback.
 */
export function useStaticTranslation<S extends SectionName>(section: S) {
  const [lang, setLang] = useState(getTranslateLang);

  useEffect(() => {
    const interval = setInterval(() => {
      const current = getTranslateLang();
      setLang(prev => prev !== current ? current : prev);
    }, 300);
    return () => clearInterval(interval);
  }, []);

  const translations = lang && lang !== "he"
    ? (allSections[section] as any)[lang] as Record<string, string> | undefined
    : null;

  const t = useCallback((key: string, hebrewFallback: string): string => {
    if (!translations) return hebrewFallback;
    return translations[key] || hebrewFallback;
  }, [translations]);

  const isHebrew = !lang || lang === "he";

  return { t, isHebrew, lang };
}
